import React, { useEffect, useLayoutEffect, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import FileIcon from "../../assets/image/file.svg";
import { mapViewToForm } from "./driverViewMap";
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import { formatTripDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatAmountNumber as formatInr } from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
import TripMeterStatusBadge from "../../Components/TripMeterStatusBadge";
import { isTripMeterStopped } from "../../utils/meterSensorIssue";

const formatDisplayDate = (yyyyMmDdOrIso) => {
  if (!yyyyMmDdOrIso) return "—";
  const s = String(yyyyMmDdOrIso).trim();
  const d = new Date(s.includes("T") ? s : `${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return s || "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatAadharDisplay = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "—";
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

const parseVehicleAvg = (v) => {
  if (v == null || v === "" || v === "-") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const formatKmPerL = (n) => {
  if (n == null) return "—";
  return `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })} KM/L`;
};

const formatVehicleAverage = (t) => {
  const n = parseVehicleAvg(t?.vehicle_average) ?? parseVehicleAvg(t?.vehicle_avg);
  return formatKmPerL(n);
};

const formatIdealAverage = (t) => {
  const n = parseVehicleAvg(t?.vehicle_id?.ideal_average) ?? parseVehicleAvg(t?.ideal_average);
  if (n == null) return "—";
  return formatKmPerL(n);
};

const mapTripHistoryRow = (t) => {
  const company = t?.company_id;
  const companyLoc =
    (typeof company === "object" && company?.company_location_id?.name) ||
    t?.company_location_id?.name ||
    t?.company_location?.name;
  return {
    id: t?._id ?? t?.id ?? `trip-${t?.trip_ref_id ?? "row"}`,
    tripId: t?.trip_ref_id != null ? String(t.trip_ref_id) : t?.trip_id ?? t?.tripId ?? "—",
    company:
      (typeof company === "object" && company?.name) ||
      t?.company?.name ||
      t?.company_name ||
      "—",
    location: companyLoc ?? t?.location ?? "—",
    miplLocation:
      t?.mipl_location_id?.name ?? t?.mipl_location?.name ?? t?.mipl_location_name ?? "—",
    tripBreak:
      t?.trip_break_location_id?.name ??
      t?.trip_break_location?.name ??
      t?.trip_break_location_name ??
      "—",
    tripStartDateTime: formatTripDateTime(
      t?.trip_start_date_time ?? t?.trip_add_date_time ?? t?.start_date ?? t?.trip_start
    ),
    tripEndDateTime: formatTripDateTime(t?.trip_end_at ?? t?.trip_end_date_time ?? t?.trip_end),
    status: Number(t?.status),
    tripAmount: formatInr(t?.trip_amount ?? t?.trip_amount_inr ?? t?.trip_amount_inr ?? 0),
    awardAmount: formatInr(t?.award_amount ?? t?.award_amount_inr ?? t?.award_amount_inr ?? 0),
    vehicle: (() => {
      const vid = t?.vehicle_id;
      if (typeof vid === "object" && vid != null) {
        const label = vid.registration_number || vid.name;
        if (label) return String(label);
      }
      return (
        t?.vehicle?.registration_number ?? t?.vehicle_number ?? "—"
      );
    })(),
    vehicle_average: formatVehicleAverage(t),
    idealAvg: formatIdealAverage(t),
    meterStopped: isTripMeterStopped(t),
  };
};

const documentTypeLabel = (documentType) => {
  const k = String(documentType ?? "").toLowerCase();
  if (k === "driver_license" || k === "license_doc" || k === "licence_doc") {
    return "Driver Licence (Front)";
  }
  if (
    k === "driver_license_back" ||
    k === "license_doc_back" ||
    k === "licence_doc_back"
  ) {
    return "Driver Licence (Back)";
  }
  if (k.includes("adhar_card_front") || k.includes("aadhar_front")) {
    return "Aadhar Card (Front)";
  }
  if (k.includes("adhar_card_back") || k.includes("aadhar_back")) {
    return "Aadhar Card (Back)";
  }
  if (k.includes("license") || k.includes("licence")) {
    if (k.includes("back")) return "Driver Licence (Back)";
    if (k.includes("front")) return "Driver Licence (Front)";
    return "Driver Licence";
  }
  if (!k) return "Document";
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const DriverDocumentCard = ({ doc, onOpenPreview }) => {
  const [imgError, setImgError] = useState(false);
  const url =
    typeof doc?.document_image === "string" ? doc.document_image.trim() : "";
  const label = documentTypeLabel(doc?.document_type);

  return (
    <div className="flex w-[30%] lg:w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="relative box-border flex h-20 lg:h-36 w-full min-h-0 shrink-0 items-center justify-center overflow-hidden bg-gray-100 p-2">
        {url && !imgError ? (
          <button
            type="button"
            className="flex h-full max-h-full w-full max-w-full min-h-0 min-w-0 cursor-pointer items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label={`View ${label}`}
            onClick={() => onOpenPreview?.({ url, label })}
          >
            <img
              src={url}
              alt={label}
              className="box-border max-h-full max-w-full object-contain"
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
            />
          </button>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
            <img src={FileIcon} className="w-8 opacity-60" alt="" />
            <span className="text-xs text-gray-500">Preview unavailable</span>
          </div>
        )}
      </div>
      <span className="border-t border-gray-100 px-2 py-2 text-center text-xs md:text-sm font-UbuntuMedium text-gray-700">
        {label}
      </span>
    </div>
  );
};

const ViewDriverDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const driverId = (searchParams.get("driver_id") || "").trim();
  const backToList = useBackToList("/driver-management");

  const [loadingDriver, setLoadingDriver] = useState(() => Boolean(driverId));
  const [driver, setDriver] = useState(null);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const [documentModal, setDocumentModal] = useState(null);

  const [tripRows, setTripRows] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [tripsTotal, setTripsTotal] = useState(0);
  const [tripsTotalPages, setTripsTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [driverId]);

  useEffect(() => {
    setDocumentModal(null);
  }, [driverId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(clampSearchInput(search).trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setProfileImageBroken(false);
  }, [driverId, driver?.profileImage]);

  useEffect(() => {
    if (!documentModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDocumentModal(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [documentModal]);

  useEffect(() => {
    setSubheading("View Driver Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!driverId) {
        setLoadingDriver(false);
        setDriver(null);
        return;
      }
      setLoadingDriver(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewDriver}?driver_id=${encodeURIComponent(driverId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const form = mapViewToForm(d);
          setDriver(form);
        } else {
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load driver";
          notifyError(errMsg);
          setDriver(null);
        }
      } catch {
        notifyError("Failed to load driver");
        setDriver(null);
      } finally {
        setLoadingDriver(false);
      }
    };
    load();
  }, [driverId]);

  useEffect(() => {
    if (!driverId) {
      setTripRows([]);
      setTripsTotal(0);
      setTripsTotalPages(1);
      setTripsLoading(false);
      return;
    }

    const loadTrips = async () => {
      setTripsLoading(true);
      try {
        const q = new URLSearchParams({
          driver_id: driverId,
          page: String(currentPage),
          limit: String(itemsPerPage),
          // search: debouncedSearch,
        });
        const response = await handleApiCall(
          "get",
          `${endpoint.listOfTripHistoryByDriver}?${q.toString()}`,
          {}
        );
        if (response?.data?.code === 200) {
          const payload = response?.data?.data || {};
          const raw =
            payload.trip_history_data ||
            payload.trip_history ||
            payload.trips ||
            [];
          const list = Array.isArray(raw) ? raw : [];
          setTripRows(list.map((row) => mapTripHistoryRow(row)));
          setTripsTotal(typeof payload.total === "number" ? payload.total : list.length);
          setTripsTotalPages(
            Math.max(1, typeof payload.total_pages === "number" ? payload.total_pages : 1)
          );
        } else {
          setTripRows([]);
          setTripsTotal(0);
          setTripsTotalPages(1);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load trip history";
          notifyError(errMsg);
        }
      } catch {
        setTripRows([]);
        setTripsTotal(0);
        setTripsTotalPages(1);
        notifyError("Failed to load trip history");
      } finally {
        setTripsLoading(false);
      }
    };
    loadTrips();
  }, [driverId, currentPage, itemsPerPage, debouncedSearch]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentTrips = tripRows;
  const totalPages = tripsTotalPages;

  const driverRefDisplay =
    driver?.driverRefId != null && String(driver.driverRefId).trim() !== ""
      ? String(driver.driverRefId)
      : "—";

  const phoneDisplay =
    driver?.mobile || driver?.countryCode
      ? `${driver.countryCode || "+91"} ${driver.mobile || ""}`.trim()
      : "—";

  if (!driverId && !loadingDriver) {
    return (
      <>
        <div className="mx-auto mt-7 max-w-5xl px-4 font-UbuntuRegular">
          <p className="text-gray-600">Missing driver id. Open view from the driver list.</p>
          <Link to={backToList} className="btn btn-primary mt-3">
            Back to list
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='col-md-4'>
      <Link to={backToList}  className="btn btn-primary shrink-0 mt-4">Back</Link>
    </div>
      {documentModal?.url ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 font-UbuntuRegular"
          role="presentation"
          onClick={() => setDocumentModal(null)}
        >
          <div
            className="flex max-h-[min(90vh,900px)] w-full max-w-4xl min-h-0 flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="driver-document-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <h4
                id="driver-document-modal-title"
                className="mb-0 text-base font-UbuntuMedium text-gray-900"
              >
                {documentModal.label}
              </h4>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={documentModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Open in new tab
                </a>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-UbuntuMedium text-gray-900 hover:bg-gray-200"
                  onClick={() => setDocumentModal(null)}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
              <div className="flex flex-1 min-h-0 items-center justify-center overflow-auto p-4 sm:p-6">
                <div className="flex min-h-[11rem] w-full max-w-full min-w-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-2 shadow-inner sm:min-h-[14rem] sm:p-4">
                  <img
                    src={documentModal.url}
                    alt={documentModal.label}
                    className="box-border max-h-[min(70vh,calc(90vh-11rem))] w-auto max-w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="lg:mt-7 text-center">
        <div className="mx-auto inline-flex h-28 w-28 md:h-40 md:w-40 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100">
          {!loadingDriver && driver?.profileImage && !profileImageBroken ? (
            <img
              src={driver.profileImage}
              alt={driver.fullName ? `${driver.fullName} profile` : "Driver profile"}
              className="h-full w-full object-cover"
              onError={() => setProfileImageBroken(true)}
            />
          ) : !loadingDriver && driver ? (
            <span className="text-4xl font-UbuntuMedium text-gray-400">
              {(driver.fullName || "?").trim().slice(0, 1).toUpperCase() || "?"}
            </span>
          ) : null}
        </div>
        {!loadingDriver && driver?.fullName ? (
          <p className="lg:mt-3 lg:mb-0 mb-2 text-lg font-UbuntuMedium text-gray-900">{driver.fullName}</p>
        ) : null}
      </div>
      <div className="mx-auto max-w-5xl lg:px-4">
        {loadingDriver ? (
          <p className="my-4 text-gray-500">Loading driver…</p>
        ) : null}
        {!loadingDriver && driverId && !driver ? (
          <p className="my-4 text-gray-500">Unable to load driver details.</p>
        ) : null}
        {driver ? (
          <>
            <h3 className="mb-2 md:text-xl font-UbuntuMedium">Personal Details</h3>
            <div className="mb-4 max-w-screen-lg rounded-lg bg-white px-3 py-2 lg:px-4 lg:py-2 shadow-sm">
              <ul className="m-0 grid list-none md:grid-cols-2 p-0 font-UbuntuMedium [&_li]:flex [&_li]:items-start [&_li]:justify-between [&_li]:gap-4 [&_li]:py-1.5 [&_li:nth-child(odd)]:border-r [&_li:nth-child(odd)]:border-gray-200 md:[&_li:nth-child(odd)]:pr-4 md:[&_li:nth-child(even)]:pl-4 lg:[&_li:nth-child(odd)]:pr-12 lg:[&_li:nth-child(even)]:pl-12 [&_.label]:shrink-0 [&_.label]:text-gray-400 [&_.value]:min-w-0 [&_.value]:text-right [&_.value]:break-words max-[767px]:text-sm">
                <li>
                  <div className="label">Driver ID</div>
                  <div className="value font-UbuntuMedium">{driverRefDisplay}</div>
                </li>
                <li>
                  <div className="label">Joining Date</div>
                  <div className="value font-UbuntuMedium">
                    {formatDisplayDate(driver.joiningDate)}
                  </div>
                </li>
                <li>
                  <div className="label">Licence Number</div>
                  <div className="value font-UbuntuMedium">{driver.licenseNumber || "—"}</div>
                </li>
                <li>
                  <div className="label">Accountant Name</div>
                  <div className="value font-UbuntuMedium">{driver.accountantName || "—"}</div>
                </li>
                <li>
                  <div className="label">NT License Expiry Date</div>
                  <div className="value font-UbuntuMedium">
                    {formatDisplayDate(driver.ntLicenseExpiry)}
                  </div>
                </li>
                <li>
                  <div className="label">Phone Number</div>
                  <div className="value font-UbuntuMedium">{phoneDisplay}</div>
                </li>
                <li>
                  <div className="label">TR License Expiry Date</div>
                  <div className="value font-UbuntuMedium">
                    {formatDisplayDate(driver.licenseExpiry)}
                  </div>
                </li>
                <li>
                  <div className="label">Aadhar Card number</div>
                  <div className="value font-UbuntuMedium">
                    {formatAadharDisplay(driver.adharNumber)}
                  </div>
                </li>
                <li>
                  <div className="label">MIPL Location</div>
                  <div className="value font-UbuntuMedium">{driver.miplLocation || "—"}</div>
                </li>
                <li>
                  <div className="label">Age</div>
                  <div className="value font-UbuntuMedium">{driver.age || "—"}</div>
                </li>
              </ul>
            </div>
            <h3 className="mb-2 md:text-xl font-UbuntuMedium">Id Proof Documents</h3>
            <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-3">
              {Array.isArray(driver.documentData) && driver.documentData.length > 0 ? (
                driver.documentData.map((doc, idx) => (
                  <DriverDocumentCard
                    key={`${doc?.document_type || "doc"}-${idx}`}
                    doc={doc}
                    onOpenPreview={setDocumentModal}
                  />
                ))
              ) : (
                <p className="text-sm text-gray-500">No document images uploaded.</p>
              )}
              <div className="rounded-lg bg-[#00CE06] p-3 md:p-4 font-UbuntuMedium max-[767px]:mt-2">
                Fingerprint Verified
              </div>
            </div>
          </>
        ) : null}
      </div>
      {/* <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 pt-2 font-UbuntuRegular">
        <input

          type="search"
          placeholder="Search by company or trip ID"
          value={search}
          maxLength={SEARCH_MAX_LENGTH}

          onChange={(e) => {
            setSearch(clampSearchInput(e.target.value));
            setCurrentPage(1);
          }}
          className="form-control max-w-xs min-h-10 flex-1 shadow-sm"
        />
      </div> */}
      <h3 className="!my-2 lg:px-4 md:text-xl font-UbuntuMedium">Complete Trip History</h3>
      <div className="common-table-wrapper m-0 shadow-sm">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Trip ID</th>
                <th>Company</th>
                <th>Location</th>
                <th>MIPL Location</th>
                <th>Trip Break</th>
                <th>Trip Start Date &amp; Time</th>
                <th>Trip End Date &amp; Time</th>
                <th>Vehicle</th>
                <th>Vehicle Avg</th>
                 <th>Ideal Avg</th>
                <th>Trip Amount</th>
                <th>Award Amount</th>
                <th>Meter</th>
              </tr>
            </thead>
            <tbody>
              {tripsLoading ? (
                <tr>
                  <td colSpan={14} className="py-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : currentTrips.length === 0 ? (
                <tr>
                  <td colSpan={14} className="py-6 text-center text-gray-500">
                    No trip history available
                  </td>
                </tr>
              ) : (
                currentTrips.map((row, index) => (
                  <tr key={row.id}>
                    <td>{indexOfFirst + index + 1}</td>
                    <td>{row.tripId}</td>
                    <td>{row.company}</td>
                    <td>{row.location}</td>
                    <td>{row.miplLocation}</td>
                    <td>{row.tripBreak}</td>
                    <td>{row.tripStartDateTime}</td>
                    <td>
                      {row.status === 4 ? (
                        <span className="font-UbuntuMedium text-red-600">Cancelled</span>
                      ) : (
                        row.tripEndDateTime
                      )}
                    </td>
                    <td>{row.vehicle}</td>
                    <td>{row.vehicle_average}</td>
                    <td>{row.idealAvg}</td>
                    <td>{row.tripAmount}</td>
                    <td>{row.awardAmount}</td>
                    <td className="whitespace-nowrap">
                      <TripMeterStatusBadge stopped={row.meterStopped} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex gap-x-4 items-center mt-4">
        <select
          className="p-2 shadow-sm outline-none max-[767px]:text-xs"
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
        <div className="ml-auto text-xs md:text-sm text-gray-500">{tripsTotal} records found</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || tripsTotal === 0 || tripsLoading}
            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center  ${
              currentPage === 1 ? "" : "cursor-pointer"
            }`}
          >
            <img src={LeftAngle} className="invert brightness-100 grayscale" alt="" />
          </button>
          <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
          <span className="text-sm text-gray-600">of {totalPages}</span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || tripsTotal === 0 || tripsLoading}
            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center cursor-pointer ${
              currentPage === totalPages ? "" : "cursor-pointer"
            }`}
          >
            <img src={RightAngle} className="invert brightness-100 grayscale" alt="" />
          </button>
        </div>
      </div>
    </>
  );
};

export default ViewDriverDetails;
